import { useEffect, useState, type FormEvent } from 'react';
import { Megaphone, Pin, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { Notice } from '../types';
import { formatTime } from '../utils';
import { ActionSheet, Button, EmptyBlock, Fab, Field, LoadingBlock, PageHeader, Panel, Textarea, TextInput } from '../components/ui';

export function NoticesPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, permissions } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<Notice[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const data = await api<{ notices: Notice[] }>('/api/notices');
    setItems(data.notices);
  };

  useEffect(() => { void load().catch(() => toast('公告加载失败', 'error')); }, [user?.id]);

  const openForm = () => {
    if (!user) return onNeedAuth();
    if (!permissions.canPublishNotice) return toast('只有管理员可以发布公告', 'error');
    setFormOpen(true);
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      await api('/api/notices', { method: 'POST', body: JSON.stringify({ title: String(form.get('title') || ''), content: String(form.get('content') || ''), pinned: form.get('pinned') === 'on' }) });
      formEl.reset();
      setFormOpen(false);
      await load();
      toast('公告已发布', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发布失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (notice: Notice) => {
    const approved = await confirm({ title: '删除公告', body: '公告将被永久删除。', danger: true, confirmText: '确认删除' });
    if (!approved) return;
    try {
      await api('/api/notices/' + notice.id, { method: 'DELETE' });
      await load();
      toast('公告已删除', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '删除失败', 'error');
    }
  };

  const togglePin = async (notice: Notice) => {
    try {
      await api('/api/notices/' + notice.id, { method: 'PATCH', body: JSON.stringify({ pinned: !notice.pinned }) });
      await load();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '修改失败', 'error');
    }
  };

  return (
    <>
      <PageHeader title="公告栏" description="管理员可以发布、置顶和删除服务器公告。" action={permissions.canPublishNotice ? <Button className="desktop-only" variant="primary" onClick={openForm}><Plus size={17} /> 发布公告</Button> : undefined} />
      <ActionSheet open={formOpen} onClose={() => setFormOpen(false)} title="发布公告" eyebrow="PUBLISH" description="发布后会立即出现在公告栏和首页。">
        <form className="stack-form" onSubmit={create}>
          <Field label="标题"><TextInput name="title" maxLength={80} required /></Field>
          <Field label="内容"><Textarea name="content" rows={5} maxLength={3000} required /></Field>
          <label className="check-line"><input name="pinned" type="checkbox" /> 置顶显示</label>
          <Button variant="primary" type="submit" loading={busy} className="wide"><Plus size={17} /> 发布公告</Button>
        </form>
      </ActionSheet>
      <Panel title="全部公告" eyebrow="NOTICE BOARD">
        {!items ? <LoadingBlock /> : items.length === 0 ? <EmptyBlock title="暂无公告" description={permissions.canPublishNotice ? '发布第一条服务器公告。' : '管理员发布后会显示在这里。'} action={permissions.canPublishNotice ? <Button variant="primary" onClick={openForm}><Megaphone size={16} /> 发布第一条公告</Button> : undefined} /> : (
          <div className="notice-list large">
            {items.map(notice => (
              <article className={'notice-row ' + (notice.pinned ? 'pinned' : '')} key={notice.id}>
                <div className="notice-marker">{notice.pinned ? '置顶' : '公告'}</div>
                <div className="notice-content">
                  <div className="notice-heading"><div><h3>{notice.title}</h3><small>{notice.authorName} · {formatTime(notice.createdAt)}</small></div>{permissions.canPublishNotice && <div className="inline-actions"><Button variant="ghost" onClick={() => void togglePin(notice)}><Pin size={15} /> {notice.pinned ? '取消置顶' : '置顶'}</Button><Button variant="danger" onClick={() => void remove(notice)}><Trash2 size={15} /> 删除</Button></div>}</div>
                  <p>{notice.content}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </Panel>
      {permissions.canPublishNotice && <Fab label="发布公告" onClick={openForm} />}
    </>
  );
}
